"use client";

import { useEffect } from "react";
import { usePathname } from "next/navigation";

import { isMarketingPath } from "@/lib/marketing-routes";
import { setReplayAllowed } from "@/lib/posthog-events";

/**
 * Decides, per page, whether session replay may run.
 *
 * Two conditions, both required: the visitor is **not signed in**, and the page
 * is on the **marketing allowlist** (lib/marketing-routes.ts). Anything else —
 * the signed-in app, a public deployed app, an unrecognised route — stops the
 * recorder.
 *
 * WHY A CLIENT COMPONENT AND NOT JUST THE SERVER FLAG. `sessionActive` comes
 * from the root layout, which re-renders on every full page load, and signing in
 * always is one (Auth0 redirects out and back), so that half cannot go stale.
 * The path half can: App Router navigations between marketing pages — and from a
 * marketing page into `/apps/...` — happen client-side with no server render, so
 * the decision has to re-run on `usePathname()`. Without this the recorder would
 * keep running as a visitor browsed from the landing page into a customer's
 * deployed app.
 *
 * Recording is off by default at the SDK level (`disable_session_recording`), so
 * a failure of this component leaves replay OFF, never on.
 */
export function PostHogReplayGate({ sessionActive }: { sessionActive: boolean }) {
  const pathname = usePathname();

  useEffect(() => {
    setReplayAllowed(!sessionActive && isMarketingPath(pathname));
  }, [sessionActive, pathname]);

  return null;
}
